import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowLeft, ArrowUp, CheckCircle2, Circle, Loader2, MapPin, Search, Shuffle, Trash2, X } from "lucide-react";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { RouteMap, type StopState } from "@/components/routes/RouteMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useTraccarSession } from "@/hooks/useTraccarSession";
import {
  createPlannedRoute,
  deletePlannedRoute,
  fetchPlannedRoute,
  updatePlannedRoute,
  type PlannedRouteInput,
  type PlannedRouteStatus,
  type RouteStop,
} from "@/lib/reseller-api";
import { fetchDevices, fetchPositions, fetchRoute } from "@/lib/traccar-api";
import { fetchOptimizedOrder, fetchRoadRoute, reverseGeocode, searchAddress, type AddressResult, type LngLat, type RoadRoute } from "@/lib/routing";
import { computeStopVisits } from "@/lib/route-progress";
import { formatDistance, formatDuration, ROUTE_STATUSES } from "@/lib/planned-routes";

const DEFAULT_RADIUS = 100;

const localDateString = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const formatTime = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export default function PlannedRouteEditor() {
  const { id = "new" } = useParams();
  const isNew = id === "new";
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useTraccarSession();
  // Readonly users can follow a route's progress but not change it.
  const canEdit = !user?.readonly;

  const [name, setName] = useState("");
  const [deviceId, setDeviceId] = useState("none");
  const [routeDate, setRouteDate] = useState(localDateString());
  const [status, setStatus] = useState<PlannedRouteStatus>("planned");
  const [notes, setNotes] = useState("");
  const [stops, setStops] = useState<RouteStop[]>([]);
  const [road, setRoad] = useState<RoadRoute | null>(null);
  const [routing, setRouting] = useState(false);
  const [routingError, setRoutingError] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [fitKey, setFitKey] = useState(0);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<AddressResult[]>([]);
  const [searching, setSearching] = useState(false);

  const { data: devices = [] } = useQuery({ queryKey: ["devices"], queryFn: fetchDevices });
  const { data: savedRoute, isLoading } = useQuery({
    queryKey: ["planned-routes", id],
    queryFn: () => fetchPlannedRoute(id),
    enabled: !isNew,
  });

  // Load the saved route into the form once.
  const loadedId = useRef<string | null>(null);
  useEffect(() => {
    if (!savedRoute || loadedId.current === savedRoute.id) return;
    loadedId.current = savedRoute.id;
    setName(savedRoute.name);
    setDeviceId(savedRoute.device_id ? String(savedRoute.device_id) : "none");
    setRouteDate(savedRoute.route_date ?? "");
    setStatus(savedRoute.status);
    setNotes(savedRoute.notes ?? "");
    setStops(savedRoute.stops);
    if (savedRoute.path) {
      setRoad({ path: savedRoute.path, distance: Number(savedRoute.distance_m) || 0, duration: Number(savedRoute.duration_s) || 0, legs: [] });
    }
    setFitKey((key) => key + 1);
  }, [savedRoute]);

  // Recompute the road path whenever the stops move.
  const stopsKey = stops.map((stop) => `${stop.longitude.toFixed(6)},${stop.latitude.toFixed(6)}`).join(";");
  useEffect(() => {
    const points = stops.map((stop) => [stop.longitude, stop.latitude] as LngLat);
    if (points.length < 2) {
      setRoad(null);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setRouting(true);
      fetchRoadRoute(points, controller.signal)
        .then((result) => {
          setRoad(result);
          setRoutingError(false);
        })
        .catch((error) => {
          if (error.name !== "AbortError") setRoutingError(true);
        })
        .finally(() => setRouting(false));
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [stopsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Address search, biased towards the last stop.
  useEffect(() => {
    const text = query.trim();
    if (text.length < 3) {
      setResults([]);
      return;
    }
    const controller = new AbortController();
    const last = stops[stops.length - 1];
    const timer = setTimeout(() => {
      setSearching(true);
      searchAddress(text, last ? [last.longitude, last.latitude] : undefined, controller.signal)
        .then(setResults)
        .catch(() => undefined)
        .finally(() => setSearching(false));
    }, 350);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  // Progress: match the vehicle's track on the route date against the stops.
  const numericDeviceId = deviceId !== "none" ? Number(deviceId) : null;
  const today = localDateString();
  const canTrack = Boolean(numericDeviceId && routeDate && routeDate <= today && stops.length);
  const { data: track = [], isFetching: loadingTrack } = useQuery({
    queryKey: ["route-track", numericDeviceId, routeDate],
    queryFn: () =>
      fetchRoute(numericDeviceId!, new Date(`${routeDate}T00:00:00`).toISOString(), new Date(`${routeDate}T23:59:59`).toISOString()),
    enabled: canTrack,
    refetchInterval: routeDate === today ? 60_000 : false,
  });
  const { data: positions = [] } = useQuery({
    queryKey: ["positions"],
    queryFn: fetchPositions,
    enabled: Boolean(numericDeviceId) && routeDate === today,
  });

  const visits = useMemo(() => (canTrack ? computeStopVisits(stops, track) : []), [canTrack, stops, track]);
  const visitedCount = visits.filter((visit) => visit.arrivedAt).length;
  const stopStates = useMemo<StopState[]>(() => {
    const firstPending = routeDate === today ? visits.findIndex((visit) => !visit.arrivedAt) : -1;
    return stops.map((_, index) => (visits[index]?.arrivedAt ? "visited" : index === firstPending ? "current" : "pending"));
  }, [routeDate, stops, today, visits]);
  const actualTrack = useMemo(() => (canTrack ? track.map((point) => [point.longitude, point.latitude] as LngLat) : []), [canTrack, track]);
  const vehiclePosition = useMemo(() => {
    const position = positions.find((p) => p.deviceId === numericDeviceId);
    return routeDate === today && position ? ([position.longitude, position.latitude] as LngLat) : null;
  }, [numericDeviceId, positions, routeDate, today]);

  // Planned cumulative distance/time to each stop, from the road legs.
  const plannedToStop = useMemo(() => {
    let distance = 0;
    let duration = 0;
    return stops.map((_, index) => {
      if (index > 0 && road?.legs[index - 1]) {
        distance += road.legs[index - 1].distance;
        duration += road.legs[index - 1].duration;
      }
      return road?.legs.length ? { distance, duration } : null;
    });
  }, [road, stops]);

  const addStop = (stop: Omit<RouteStop, "radius">) => {
    setStops((current) => [...current, { radius: DEFAULT_RADIUS, ...stop }]);
  };

  const addStopFromMap = (point: LngLat) => {
    const index = stops.length;
    addStop({ name: `${t("routes.stop")} ${index + 1}`, latitude: point[1], longitude: point[0], address: null });
    reverseGeocode(point).then((result) => {
      if (!result) return;
      setStops((current) =>
        current.map((stop, i) =>
          i === index && stop.latitude === point[1] && stop.longitude === point[0]
            ? { ...stop, name: result.name || stop.name, address: result.address }
            : stop,
        ),
      );
    });
  };

  const updateStop = (index: number, patch: Partial<RouteStop>) =>
    setStops((current) => current.map((stop, i) => (i === index ? { ...stop, ...patch } : stop)));

  const moveStop = (index: number, delta: number) =>
    setStops((current) => {
      const target = index + delta;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const optimize = async () => {
    setOptimizing(true);
    try {
      const order = await fetchOptimizedOrder(stops.map((stop) => [stop.longitude, stop.latitude]), false);
      setStops((current) => order.map((index) => current[index]));
      toast({ title: t("routes.optimized") });
    } catch (error) {
      toast({ title: t("common.error"), description: (error as Error).message, variant: "destructive" });
    } finally {
      setOptimizing(false);
    }
  };

  const saveMutation = useMutation({
    mutationFn: (payload: PlannedRouteInput) => (isNew ? createPlannedRoute(payload) : updatePlannedRoute(id, payload)),
    onSuccess: (route) => {
      queryClient.invalidateQueries({ queryKey: ["planned-routes"] });
      toast({ title: isNew ? t("routes.created") : t("routes.updated") });
      if (isNew) navigate(`/routes/${route.id}`, { replace: true });
    },
    onError: (error: Error) => toast({ title: t("common.error"), description: error.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deletePlannedRoute(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["planned-routes"] });
      toast({ title: t("routes.deleted") });
      navigate("/routes");
    },
    onError: (error: Error) => toast({ title: t("common.error"), description: error.message, variant: "destructive" }),
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast({ title: t("routes.nameRequired"), variant: "destructive" });
      return;
    }
    saveMutation.mutate({
      name: name.trim(),
      device_id: numericDeviceId,
      route_date: routeDate || null,
      status,
      notes: notes.trim() || null,
      stops,
      path: road?.path ?? null,
      distance_m: road?.distance ?? null,
      duration_s: road?.duration ?? null,
    });
  };

  if (!isNew && isLoading) {
    return (
      <DashboardLayout>
        <div className="p-6 text-muted-foreground">{t("common.loading")}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex flex-col lg:flex-row h-full">
        <div className="w-full lg:w-[420px] lg:border-r lg:overflow-y-auto p-4 space-y-4 shrink-0">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/routes")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-bold flex-1">{isNew ? t("routes.create") : t("routes.edit")}</h1>
            {!isNew && canEdit && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (confirm(t("reseller.common.deleteConfirm"))) deleteMutation.mutate();
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Disables every input for readonly users (fieldset disables nested controls). */}
          <fieldset disabled={!canEdit} className="space-y-4 min-w-0">
          <div className="space-y-2">
            <Label htmlFor="route_name">{t("devices.name")} *</Label>
            <Input id="route_name" value={name} onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="grid gap-3 grid-cols-2">
            <div className="space-y-2 col-span-2">
              <Label htmlFor="route_device">{t("reseller.fieldService.vehicle")}</Label>
              <Select value={deviceId} onValueChange={setDeviceId}>
                <SelectTrigger id="route_device">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("reseller.fieldService.noVehicle")}</SelectItem>
                  {devices.map((device) => (
                    <SelectItem key={device.id} value={String(device.id)}>{device.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="route_date">{t("routes.date")}</Label>
              <Input id="route_date" type="date" value={routeDate} onChange={(event) => setRouteDate(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="route_status">{t("routes.statusLabel")}</Label>
              <Select value={status} onValueChange={(value) => setStatus(value as PlannedRouteStatus)}>
                <SelectTrigger id="route_status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROUTE_STATUSES.map((value) => (
                    <SelectItem key={value} value={value}>{t(`routes.status.${value}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border p-3 grid grid-cols-3 gap-2 text-center text-sm">
            <div>
              <div className="text-muted-foreground text-xs">{t("routes.stops")}</div>
              <div className="font-semibold">{stops.length}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">{t("routes.distance")}</div>
              <div className="font-semibold">{routing ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : formatDistance(road?.distance)}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">{t("routes.duration")}</div>
              <div className="font-semibold">{routing ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : formatDuration(road?.duration)}</div>
            </div>
          </div>
          {routingError && <p className="text-xs text-destructive">{t("routes.routingFailed")}</p>}

          {canTrack && (
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span>{t("routes.progress")}</span>
                <span className="text-muted-foreground">
                  {loadingTrack && !track.length ? <Loader2 className="h-3 w-3 animate-spin inline" /> : t("routes.visitedOf", { visited: visitedCount, total: stops.length })}
                </span>
              </div>
              <Progress value={stops.length ? (visitedCount / stops.length) * 100 : 0} />
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t("routes.stops")}</Label>
              <Button variant="outline" size="sm" onClick={optimize} disabled={stops.length < 3 || optimizing}>
                {optimizing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Shuffle className="h-3.5 w-3.5 mr-1.5" />}
                {t("routes.optimize")}
              </Button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("routes.searchAddress")}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="pl-9 pr-9"
              />
              {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
              {!searching && query && (
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setQuery("")}>
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
              {results.length > 0 && (
                <div className="absolute z-20 mt-1 w-full rounded-md border bg-popover shadow-md max-h-64 overflow-y-auto">
                  {results.map((result, index) => (
                    <button
                      key={`${result.latitude},${result.longitude},${index}`}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
                      onClick={() => {
                        addStop(result);
                        setQuery("");
                        setResults([]);
                        setFitKey((key) => key + 1);
                      }}
                    >
                      <div className="font-medium truncate">{result.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{result.address}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{t("routes.clickMapHint")}</p>

            {stops.length === 0 ? (
              <div className="text-center py-6 text-sm text-muted-foreground border rounded-lg border-dashed">
                <MapPin className="h-5 w-5 mx-auto mb-1" />
                {t("routes.noStops")}
              </div>
            ) : (
              <ol className="space-y-2">
                {stops.map((stop, index) => {
                  const visit = visits[index];
                  const planned = plannedToStop[index];
                  return (
                    <li key={`${index}-${stop.latitude}-${stop.longitude}`} className="rounded-lg border p-2 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${
                            stopStates[index] === "visited" ? "bg-green-600" : stopStates[index] === "current" ? "bg-amber-500" : "bg-blue-500"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <Input value={stop.name} onChange={(event) => updateStop(index, { name: event.target.value })} className="h-8" />
                        <div className="flex shrink-0">
                          <Button variant="ghost" size="icon" className="h-8 w-8" disabled={index === 0} onClick={() => moveStop(index, -1)}>
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" disabled={index === stops.length - 1} onClick={() => moveStop(index, 1)}>
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setStops((current) => current.filter((_, i) => i !== index))}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      {stop.address && <p className="text-xs text-muted-foreground truncate pl-8">{stop.address}</p>}
                      <div className="flex items-center gap-3 pl-8 text-xs text-muted-foreground">
                        <label className="flex items-center gap-1">
                          {t("routes.radius")}
                          <Input
                            type="number"
                            min="10"
                            step="10"
                            value={stop.radius}
                            onChange={(event) => updateStop(index, { radius: Math.max(10, Number(event.target.value) || DEFAULT_RADIUS) })}
                            className="h-6 w-16 px-1.5 text-xs"
                          />
                          m
                        </label>
                        {planned && index > 0 && (
                          <span>
                            {formatDistance(planned.distance)} · {formatDuration(planned.duration)}
                          </span>
                        )}
                        {canTrack && (
                          <span className="ml-auto flex items-center gap-1">
                            {visit?.arrivedAt ? (
                              <>
                                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                {formatTime(visit.arrivedAt)}
                                {visit.departedAt && `–${formatTime(visit.departedAt)}`}
                              </>
                            ) : (
                              <>
                                <Circle className="h-3.5 w-3.5" />
                                {t("routes.notVisited")}
                              </>
                            )}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="route_notes">{t("reseller.fieldService.notes")}</Label>
            <Textarea id="route_notes" value={notes} onChange={(event) => setNotes(event.target.value)} rows={2} />
          </div>

          </fieldset>

          {canEdit && (
            <Button className="w-full" onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isNew ? t("common.create") : t("common.save")}
            </Button>
          )}
        </div>

        <div className="flex-1 min-h-[420px]">
          <RouteMap
            stops={stops}
            stopStates={stopStates}
            plannedPath={road?.path}
            actualTrack={actualTrack}
            vehiclePosition={vehiclePosition}
            onMapClick={canEdit ? addStopFromMap : undefined}
            fitKey={fitKey}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}
